import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, SecurityContext } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { ProfileDetails } from 'src/app/components/interfaces/myStories.interface';
import { EventInfo } from 'src/app/interfaces/event-info.interface';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class MyStoriesService {

  private _participantInfo: EventInfo = <any>null;
  private userProfileDetails: ProfileDetails = <any>null;
  private _ciamId: string = '';
  eventIdOfEvent!: string | null;
  vinOrFinOfCar!: string | null;

  constructor(private readonly _httpClient: HttpClient, private readonly sanitizer: DomSanitizer) { }

  public set particpantInfoResponse(data : EventInfo) {
    this._participantInfo = data;
  }

  public get particpantInfoResponse(): EventInfo {
    return this._participantInfo;
  }

  private readonly editProfileClickedSubject = new BehaviorSubject<boolean>(false);
  private readonly editProfileClosedSubject = new BehaviorSubject<boolean>(false);

  private readonly _isProfileUpdated$ = new BehaviorSubject<boolean>(false);

  editProfileClicked$ = this.editProfileClickedSubject.asObservable();
  editProfileClosed$ = this.editProfileClosedSubject.asObservable();


  private readonly showSpinnerSubject = new BehaviorSubject<boolean>(false);
  readonly showSpinnerSubject$ = this.showSpinnerSubject.asObservable();

  private readonly showEmptyEventGarageSubject = new BehaviorSubject<boolean>(false);
  readonly showEmptyEventGarageSubject$ = this.showEmptyEventGarageSubject.asObservable();

  private readonly emptyCarGarageSubject = new BehaviorSubject<boolean>(false);
  readonly emptyCarGarageSubject$ = this.emptyCarGarageSubject.asObservable();

  private readonly showUrlIncludesEventsSubject = new BehaviorSubject<boolean>(false);
  readonly showUrlIncludesEventsSubject$ = this.showUrlIncludesEventsSubject.asObservable();

  showUrlIncludesEvents(): void {
    this.showUrlIncludesEventsSubject.next(true); // Emit true when called
  }

  hideUrlIncludesEvents(): void {
    this.showUrlIncludesEventsSubject.next(false); // Emit false when called
  }

  public showEventGarage(): void {
    this.showEmptyEventGarageSubject.next(true);
  }

  public hideEventGarage(): void {
    this.showEmptyEventGarageSubject.next(false);
  }

  public setCarGarageEmpty(isEmpty: boolean): void {
    this.emptyCarGarageSubject.next(isEmpty);
  }

  public showSpinner(): void {
    this.showSpinnerSubject.next(true);
  }

  public hideSpinner(): void {
    this.showSpinnerSubject.next(false);
  }

  public editProfilePopupVisible(visible: boolean): void {
    this.editProfileClickedSubject.next(visible);
  }

  public setIsProfileUpdate(isUpdated: boolean): void {
    this._isProfileUpdated$.next(isUpdated)
  }

  public getIsProfileUpdated(): Observable<boolean> {
    return this._isProfileUpdated$.asObservable()
  }

  public editProfilePopupClosed(): void {
    this.editProfileClosedSubject.next(true);
  }

  public set profileInfoResponse(data: ProfileDetails) {
    this.userProfileDetails = data;
  }

  public get profileInfoResponse(): ProfileDetails {
    return this.userProfileDetails;
  }

  public set ciamId(userId: string) {
    this._ciamId = userId;
  }

  public get ciamId(): string{
    return this._ciamId;
  }


  // getter and setter for setting vin and eventId
  set eventId(id: string | null) {
    this.eventIdOfEvent = id;
  }

  get eventId(): string | null {
    return this.eventIdOfEvent;
  }

  set vinOrFin(id: string | null) {
    this.vinOrFinOfCar = id;
  }
  get vinOrFin(): string | null {
    return this.vinOrFinOfCar;
  }

  /**
   * @description Fetches garage details for a given user
   * @param ciamId
   * @returns Observable
   */
  public getGarageDetails(ciamId?: any): Observable<any> {
    const headers = new HttpHeaders()
      .set('ciamId', ciamId)
      .set('Content-Type', 'application/json');
    let url = ''
    if (localStorage.getItem('isLoggedIn') === 'true') {
      url = environment.proxyEndpoint + '/stories/getGarageData';
    } else {
      url = environment.proxyEndpoint + '/stories/getPublicGarageData';
    }
    const body = {
      "latestTimestamp": 0
    }
    return this._httpClient.post(url, body, { headers: headers, withCredentials: true });
  }

  /**
   * @description Fetches stories for a given VIN or FIN
   * @param vinOrFin
   * @param ciamId
   * @param pageNumber
   * @param pageSize
   * @returns Obserbable
   */
  public getStroiesForVin(vinOrFin: any, ciamId: any, pageNumber: number, pageSize: number): Observable<any>{
    let api_header = {
      headers: { 'Content-Type': 'application/json', 'vinOrFin': vinOrFin, 'ciamId': ciamId },
    };
    let url = '';
    if (localStorage.getItem('isLoggedIn') === 'true') {
      url = `${environment.proxyEndpoint}/stories/getStories?pageSize=${pageSize}&pageNumber=${pageNumber}`
    } else {
      url = `${environment.proxyEndpoint}/stories/getPublicStoriesForCar?pageSize=${pageSize}&pageNumber=${pageNumber}`
    }
    return this._httpClient.get<any>(url, {headers: api_header.headers, withCredentials: true});
  }

  /**
   * @description Fetches user details and displays in header, garage and edit profile
   * @param userId
   * @returns Observable
   */
  public getProfile(userId?: any): Observable<any> {
    let api_header = {headers: {}};
    if (userId) {
      api_header = {
        headers: {'userId': userId}
      };
    }
    let url = '';
    if (localStorage.getItem('isLoggedIn') === 'true') {
      url = `${environment.proxyEndpoint}/stories/getUser?isStories=1`;
    } else {
      url = `${environment.proxyEndpoint}/stories/getPublicProfileData`;
    }
    return this._httpClient.get<any>(url, {headers: api_header.headers, withCredentials: true});
  }

  /**
   * @description This API is called in /register/:eventId route
   * Fetches user details and displays in slef-regsitration flow
   * @param eventId
   * @returns Observable
   */
  public getUserInfoForSelfRegistration(eventId: string): Observable<any>{
    const baseUrl = environment.proxyEndpoint
    const url= `${baseUrl}/stories/events/fetchRegistrationDetails/${eventId}`;
    return this._httpClient.get(url, {withCredentials: true});
  }

  /**
   * @description This API is called on click of register button in Self-registration flow
   * @param: Event ID
   */
  public registerUser(eventId: string): Observable<any>{
    const baseUrl = environment.proxyEndpoint
    const url= `${baseUrl}/stories/events/registerUser/${eventId}`;
    return this._httpClient.post(url,{}, {withCredentials: true});
  }

  /**
   * @description This API is called on click of Update button in edit profile popup.
   * Can update nickname, description and social link
   * @param nickName
   * @param description
   * @param socialLink
   * @param userId
   * @returns Observablef
   */
  public updateProfile(nickName: string, description: string, socialLink: string, userId: any): Observable<any> {
    const api_header = { 'Content-Type': 'application/json', 'userId': userId};
    const baseUrl= environment.proxyEndpoint +'/stories/updateUser?isStories=1';
    const url=`${baseUrl}`;
    return this._httpClient.put(url,{nickName, description, socialLink},{headers: api_header, withCredentials: true});
  }

  /**
   * @description called post successful CIAM login; will have customer details and
   * event details. Called when we reach the link-participant page
   * @param participantId
   * @param participantKey
   * @returns Participant details and event details
   */
  public getParticipantInfo(participantId: any, participantKey: any): Observable<any>{
    const baseUrl = environment.proxyEndpoint
    const url = `${baseUrl}/stories/participants/getParticipantById/${participantKey}/${participantId}`
    return this._httpClient.get(url, {withCredentials: true});
  }

  /**
   * @description called on confirm click and refresh
   * @param participantId
   * @param participantKey
   * @returns event data
   */
  public updateCiamConfirmation(participantId: any, participantKey:any): Observable<any> {
    const baseUrl = environment.proxyEndpoint;
    const url = `${baseUrl}/stories/participants/confirmParticipantLink/${participantKey}/${participantId}`
    return this._httpClient.put(url,{}, {withCredentials: true});
  }

  public isValidGuID(guID: any){
    const regex = /^[{]?[0-9a-fA-F]{8}-([0-9a-fA-F]{4}-){3}[0-9a-fA-F]{12}[}]?$/
    return regex.test(guID)
  }
  public getEventDetails(eventId: any): Observable<any>{
    let api_header = {};
    if (eventId) {
      api_header = {
        headers: {'eventid':eventId}
      };
    }

    const baseUrl = environment.proxyEndpoint
    const url= `${baseUrl}/stories/events/getEventData`;
    return this._httpClient.get(url,api_header);
  }

  public getCIAMandEventInfo(eventId: any): Observable<any>{
    const baseUrl = environment.proxyEndpoint
    const url= `${baseUrl}/stories/events/getCIAMandEventInfo/${eventId}`;
    return this._httpClient.get(url,{withCredentials: true});
  }

  public getEventInfoForVerify(participantId: any, participantSecretKey:any): Observable<any>{
    participantId = this.sanitizer.sanitize(SecurityContext.URL, participantId)
    participantSecretKey = this.sanitizer.sanitize(SecurityContext.URL, participantSecretKey)
    const baseUrl = environment.proxyEndpoint
    const url= `${baseUrl}/stories/events/getEventInfo/${participantSecretKey}/${participantId}`;
    return this._httpClient.get(url,{withCredentials: true});
  }

  public getEventDetailsInfoForVerify(participantId: any, participantSecretKey:any): Observable<any>{
    participantId = this.sanitizer.sanitize(SecurityContext.URL, participantId)
    participantSecretKey = this.sanitizer.sanitize(SecurityContext.URL, participantSecretKey)
    const baseUrl = environment.proxyEndpoint
    const url= `${baseUrl}/stories/events/getEventCIAMLinkDetailsInfo/${participantSecretKey}/${participantId}`;
    return this._httpClient.get(url,{withCredentials: true});
  }

  public verifyParticipant(participantId: any, participantSecretKey:any): Observable<any>{
    participantId = this.sanitizer.sanitize(SecurityContext.URL, participantId)
    participantSecretKey = this.sanitizer.sanitize(SecurityContext.URL, participantSecretKey)
    const baseUrl = environment.proxyEndpoint
    const url= `${baseUrl}/stories/events/verifyParticipant/${participantId}/${participantSecretKey}`;
    return this._httpClient.put(url,{},{withCredentials: true});
  }

  public registerForEvent(eventId: any): Observable<any>{
    const baseUrl = environment.proxyEndpoint
    const url= `${baseUrl}/stories/events/selfRegistration/${eventId}`;
    return this._httpClient.post(url,{},{withCredentials: true});
  }


  public getParticipantQrForSelfReg(eventId: any, participantId:any): Observable<any> {
    const baseUrl = environment.proxyEndpoint;
   const url = `${baseUrl}/stories/events/getParticipantQR/${eventId}/${participantId}`

    return this._httpClient.get(url,{withCredentials: true, responseType: 'blob'});
  }

  public getAllEventIds(ciamId: any): Observable<any>{
    const headers = new HttpHeaders()
      .set('ciamId', ciamId)
      .set('Content-Type', 'application/json');
     let url = ''
    if (localStorage.getItem('isLoggedIn') === 'true') {
      url = environment.proxyEndpoint + '/stories/events/getAllEventsData';
    } else {
      url = environment.proxyEndpoint + '/stories/events/getPublicEventsData';
    }
    const body = {
      "latestTimestamp": 0
    }
    return this._httpClient.post(url, body, {headers: headers, withCredentials: true});
  }

  public getAllEventsData(eventIds: any, ciamId: any): Observable<any>{
    const headers = new HttpHeaders()
      .set('ciamId', ciamId)
      .set('Content-Type', 'application/json');
     let url = '';
      if (localStorage.getItem('isLoggedIn') === 'true') {
      url = environment.proxyEndpoint + '/stories/events/getEventInfoForEventIds';
    } else {
      url = environment.proxyEndpoint + '/stories/events/getPublicEventInfoForEventIds';
    }
    const body = {
    "eventIds": eventIds
    }
    return this._httpClient.post(url, body, {headers: headers, withCredentials: true});
  }

  public getAllStoriesForEventIds(eventId: any, pageNumber: number, pageSize: number, ciamId: any): Observable<any>{
    const headers = new HttpHeaders()
      .set('ciamId', ciamId)
      .set('Content-Type', 'application/json');
     let url = '';
    if (localStorage.getItem('isLoggedIn') === 'true') {
      url = `${environment.proxyEndpoint}/stories/events/getAllStoriesForEventIds?eventId=${eventId}&page_size=${pageSize}&page_number=${pageNumber}`
    } else {
      url = `${environment.proxyEndpoint}/stories/events/getAllPublicStoriesForEventIds?eventId=${eventId}&page_size=${pageSize}&page_number=${pageNumber}`
    }
    return this._httpClient.get<any>(url, {headers: headers, withCredentials: true});
  }


}
